import type { ReactNode } from "react";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import { Icon } from "./Icon.tsx";
import { Pressable } from "./Pressable.tsx";
import { Text } from "./Text.tsx";
import { cx } from "./tokens.ts";
import type { MenuEntry } from "./types.ts";

export type ListRowTree = {
  /** Nesting level (0 = top); each level indents by space 4. */
  depth: number;
  /** Defined = the row has children: a chevron (open when true). Undefined = a leaf (the slot stays empty). */
  expanded?: boolean;
  onToggle?: () => void;
  /** Name of the chevron (default "Expand" / "Collapse"). */
  toggleLabel?: string;
};

export type ListRowProps = {
  title: string;
  /** Second line (regular density only): caption, text.secondary. */
  subtitle?: string;
  /**
   * An Icon (icon.sm) or Avatar (avatar.xs in compact, up to avatar.md in regular), centred in a FIXED-width slot so
   * channel icons, DM avatars and page icons put every title at the same x.
   */
  leading?: ReactNode;
  /** Keep the empty leading slot (rows without an icon in a list where others have one). Default: when `leading` is set. */
  reserveLeading?: boolean;
  /** Right after the title, never truncated (a person's status emoji); the title truncates before it. */
  titleAccessory?: ReactNode;
  /** Badge, Icon or Chip after the title. */
  trailing?: ReactNode;
  /** Extra trailing buttons (IconButton / Menu): web shows them on hover or focus; touch and native always. */
  actions?: ReactNode;
  onPress: () => void;
  /** The open item: `aria-current="page"`, selected background, primary text. */
  current?: boolean;
  /** Unread: semibold, primary text. */
  emphasized?: boolean;
  /** Muted (e.g. a muted channel): tertiary text. */
  muted?: boolean;
  /** "compact" (default): sidebar rows, footnote. "regular": rows in sheets and screens, body text and a subtitle. */
  density?: "compact" | "regular";
  /** Docs tree: indent and a disclosure slot before the leading slot. */
  tree?: ListRowTree;
  contextMenu?: MenuEntry[];
  accessibilityLabel?: string;
  /** The row; its chevron is `<testID>-toggle`. */
  testID: string;
};

/** One row of a list or the sidebar: [indent][disclosure][leading] title · subtitle [trailing][actions]. */
export function ListRow(p: ListRowProps) {
  const t = usePrimitiveT();
  const density = p.density ?? "compact";
  const reserve = p.reserveLeading ?? p.leading !== undefined;
  const tree = p.tree;
  const strong = p.current === true || p.emphasized === true;
  const title = (
    <Text
      variant={density === "compact" ? "footnote" : "body"}
      weight={p.emphasized === true ? "semibold" : undefined}
      color={strong ? "text.primary" : p.muted === true ? "text.tertiary" : density === "compact" ? "text.secondary" : "text.primary"}
      lines={1}
    >
      {p.title}
    </Text>
  );
  return (
    <Pressable direction="horizontal" radius="md" current={p.current} onPress={p.onPress} contextMenu={p.contextMenu} accessibilityLabel={p.accessibilityLabel} testID={p.testID}>
      <span
        className={cx("b-row", `b-row-${density}`, "b-hover-group")}
        style={tree === undefined || tree.depth <= 0 ? undefined : { paddingInlineStart: `calc(var(--b-row-padding-x) + var(--b-space-4) * ${Math.floor(tree.depth)})` }}
      >
        {tree === undefined ? null : (
          <span className="b-row-disclosure">
            {tree.expanded === undefined || tree.onToggle === undefined ? null : (
              <button
                type="button"
                className="b-row-disclosure-button"
                aria-expanded={tree.expanded}
                aria-label={tree.toggleLabel ?? (tree.expanded ? t("primitive.listRow.collapse") : t("primitive.listRow.expand"))}
                onClick={(e) => {
                  e.stopPropagation();
                  tree.onToggle?.();
                }}
                data-testid={`${p.testID}-toggle`}
              >
                <Icon name={tree.expanded ? "chevronDown" : "chevronRight"} size="icon.sm" />
              </button>
            )}
          </span>
        )}
        {reserve ? <span className="b-row-leading">{p.leading}</span> : null}
        <span className="b-row-text">
          {p.titleAccessory === undefined || p.titleAccessory === null ? title : (
            <span className="b-row-title">
              {title}
              {p.titleAccessory}
            </span>
          )}
          {p.subtitle === undefined || density === "compact" ? null : (
            <Text variant="caption" color="text.secondary" lines={1}>
              {p.subtitle}
            </Text>
          )}
        </span>
        {p.trailing === undefined ? null : <span className="b-row-trailing">{p.trailing}</span>}
        {p.actions === undefined ? null : <span className="b-row-trailing b-reveal">{p.actions}</span>}
      </span>
    </Pressable>
  );
}
